var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jwtDecode } from 'jwt-decode';
import { UserManager, WebStorageStateStore } from 'oidc-client-ts';
import { createApiClient } from '../api/createApiClient';
class OidcUser {
    constructor(user) {
        this.user = user;
    }
    get attributes() {
        return this.user.profile;
    }
    get username() {
        return this.user.profile.sub;
    }
    get displayName() {
        return this.user.profile.name || this.user.profile.email || this.user.profile.sub;
    }
    get underlyingUserObject() {
        return this.user;
    }
}
class OidcSession {
    constructor(idToken) {
        this.idToken = idToken;
    }
    getIdToken() {
        return this.idToken;
    }
}
class OidcIdToken {
    constructor(jwt) {
        this.jwt = jwt;
        this.payload = jwtDecode(jwt);
    }
    getJwtToken() {
        return this.jwt;
    }
    getExpiration() {
        return this.payload['exp'];
    }
    getIssuedAt() {
        return this.payload['iat'];
    }
}
class CookieStore {
    constructor(domain = undefined, prefix = 'oidc_client') {
        this.domain = domain;
        this.prefix = prefix;
        this._keys = [];
        for (const kvp of document.cookie.split(';')) {
            const [key, value] = kvp.trim().split('=');
            if (key.startsWith(prefix)) {
                const subKey = key.substring(prefix.length + 1);
                if (!(subKey in this)) {
                    this[subKey] = value;
                    this._keys.push(subKey);
                }
            }
        }
    }
    get length() {
        return this._keys.length;
    }
    clear() {
        for (const k of this._keys) {
            delete this[k];
            this._keys.splice(0, this._keys.length);
            this.clearCookie(k);
        }
    }
    getItem(key) {
        if (this._keys.includes(key)) {
            return this[key];
        }
        else {
            return null;
        }
    }
    key(index) {
        if (index >= 0 && index < this._keys.length) {
            return this[this._keys[index]];
        }
        else {
            return null;
        }
    }
    removeItem(key) {
        const ix = this._keys.indexOf(key);
        if (ix >= 0) {
            delete this[key];
            this._keys.splice(ix, 1);
            this.clearCookie(key);
        }
    }
    clearCookie(key) {
        if (this.domain) {
            document.cookie = `${this.prefix}_${key}=; domain=${this.domain}; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; secure; samesite=strict`;
        }
        else {
            document.cookie = `${this.prefix}_${key}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; secure; samesite=strict`;
        }
    }
    setItem(key, value) {
        const ix = this._keys.indexOf(key);
        let setCookie = false;
        if (ix >= 0) {
            setCookie = true;
            this[key] = value;
        }
        else {
            // Ignore items that conflict with reserved words (method names)
            if (!(key in this)) {
                setCookie = true;
                this[key] = value;
                this._keys.push(key);
            }
        }
        if (setCookie) {
            // expires in 1 week. the OIDC token expiration would take precedence if shorter
            const expires = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toUTCString();
            if (this.domain) {
                document.cookie = `${this.prefix}_${key}=${value}; domain=${this.domain}; path=/; expires=${expires}; secure; samesite=strict`;
            }
            else {
                document.cookie = `${this.prefix}_${key}=${value}; path=/; expires=${expires}; secure; samesite=strict`;
            }
        }
    }
}
export class KeycloakAuthenticationManager {
    constructor(config) {
        this.config = config;
        this.accountLinks = {
            parent: this,
            get signUpLink() {
                return `${this.parent.config.authority}/protocol/openid-connect/registrations?client_id=${this.parent.config.client_id}&response_type=code&scope=openid%20email&redirect_uri=${window.location.href}&kc_locale=en`;
            },
            get forgotPasswordLink() {
                return `${this.parent.config.authority}/login-actions/reset-credentials?client_id=${this.parent.config.client_id}`;
            },
        };
        this.userManager = new UserManager({
            authority: config.authority,
            client_id: config.client_id,
            scope: 'openid profile email offline_access',
            redirect_uri: config.redirect_uri,
            automaticSilentRenew: true,
            userStore: new WebStorageStateStore({ store: new CookieStore(config.cookieDomain) }),
        });
    }
    currentAuthenticatedUser() {
        return __awaiter(this, void 0, void 0, function* () {
            const user = yield this.userManager.getUser();
            if ((user === null || user === void 0 ? void 0 : user.expires_at) && new Date(user.expires_at * 1000) > new Date()) {
                return new OidcUser(user);
            }
        });
    }
    currentSession() {
        return __awaiter(this, void 0, void 0, function* () {
            const user = yield this.userManager.getUser();
            if ((user === null || user === void 0 ? void 0 : user.id_token) && (user === null || user === void 0 ? void 0 : user.expires_at) && new Date(user.expires_at * 1000) > new Date()) {
                return new OidcSession(new OidcIdToken(user.id_token));
            }
        });
    }
    signIn(email, password) {
        return __awaiter(this, void 0, void 0, function* () {
            const user = yield this.userManager.signinResourceOwnerCredentials({
                username: email,
                password: password,
            });
            return new OidcUser(user);
        });
    }
    signOut() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.userManager.removeUser();
            window.location.href = '/login';
        });
    }
    changePassword(user, oldPassword, newPassword) {
        return __awaiter(this, void 0, void 0, function* () {
            const torqCloudApiClient = createApiClient(this, this.config.torqCloudBaseUrl, { 'Content-type': 'application/json' });
            yield torqCloudApiClient.post(`/api/v1/users/change_password/`, { oldPassword, newPassword });
        });
    }
}
//# sourceMappingURL=Keycloak.js.map